/**
 * InvoiceList — paginated invoice history (design Screen 4, bottom table).
 *
 * Owns its own `useInvoices()` call (cursor pagination). Renders a real
 * `<table>` with `<th scope="col">` for accessibility (design Screen 4 a11y),
 * one `<tr data-testid="invoice-row">` per invoice with: date, amount
 * (cents → $), a status badge (icon + text — color is never the only signal),
 * and a "View" link to the Stripe-hosted invoice page. Surfaces empty, error,
 * loading, and "Load more" states.
 *
 * Reads these fields from each invoice (per the pinned contract):
 *   created_at, amount_cents, currency, status, hosted_invoice_url.
 */

import React from 'react';
import { useInvoices } from '../../hooks/useInvoices';
import styles from './InvoiceList.module.css';

/** Format an amount in minor units (cents) to a localized currency string. */
function formatAmount(amountCents, currency = 'usd') {
  const cents = typeof amountCents === 'number' ? amountCents : 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch {
    // Unknown currency code — fall back to a plain dollar format.
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/** Format an ISO date string as e.g. "Jun 13, 2026". */
function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Status → { label, tone, icon } for the badge (icon + text + color). */
const STATUS_META = {
  paid: { label: 'Paid', tone: 'success', icon: '✓' },
  open: { label: 'Open', tone: 'info', icon: '•' },
  past_due: { label: 'Past due', tone: 'warning', icon: '⚠' },
  uncollectible: { label: 'Uncollectible', tone: 'error', icon: '✕' },
  void: { label: 'Void', tone: 'neutral', icon: '–' },
  draft: { label: 'Draft', tone: 'neutral', icon: '–' },
};

function statusMeta(status) {
  return STATUS_META[status] || { label: status || 'Unknown', tone: 'neutral', icon: '•' };
}

export default function InvoiceList() {
  const { invoices, loading, error, hasMore, loadMore, refetch } = useInvoices();

  // First-load skeleton (no rows yet).
  if (loading && invoices.length === 0) {
    return (
      <div className={styles.state} data-testid="invoice-list" aria-busy="true">
        <p className={styles.muted}>Loading invoices…</p>
      </div>
    );
  }

  // Error with no rows — inline alert + retry (design Screen 4 / §7).
  if (error && invoices.length === 0) {
    return (
      <div className={styles.state} data-testid="invoice-list">
        <div className={styles.error} role="alert">
          <span>Couldn't load invoices.</span>
          <button
            type="button"
            className={styles.retry}
            onClick={refetch}
            data-testid="invoice-list-retry"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty (no invoices yet).
  if (invoices.length === 0) {
    return (
      <div className={styles.state} data-testid="invoice-list">
        <p className={styles.muted}>
          No invoices yet — your first charge will appear here after your trial
          ends.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="invoice-list">
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <caption className={styles.srOnly}>Your invoice history</caption>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Amount</th>
              <th scope="col">Status</th>
              <th scope="col">Receipt</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => {
              const meta = statusMeta(inv.status);
              return (
                <tr key={inv.id || inv.stripe_invoice_id} data-testid="invoice-row">
                  <td data-label="Date">{formatDate(inv.created_at)}</td>
                  <td data-label="Amount" className={styles.amount}>
                    {formatAmount(inv.amount_cents, inv.currency)}
                  </td>
                  <td data-label="Status">
                    <span className={`${styles.badge} ${styles[`tone_${meta.tone}`]}`}>
                      <span className={styles.badgeIcon} aria-hidden="true">
                        {meta.icon}
                      </span>
                      {meta.label}
                    </span>
                  </td>
                  <td data-label="Receipt">
                    {inv.hosted_invoice_url ? (
                      <a
                        className={styles.viewLink}
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                        <span className={styles.srOnly}> invoice (opens in a new tab)</span>
                      </a>
                    ) : (
                      <span className={styles.muted}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* A row-level error after at least one page loaded — non-blocking. */}
      {error && (
        <div className={styles.errorInline} role="alert">
          Couldn't load more invoices.{' '}
          <button type="button" className={styles.retry} onClick={loadMore}>
            Retry
          </button>
        </div>
      )}

      {hasMore && (
        <div className={styles.loadMoreWrap}>
          <button
            type="button"
            className={styles.loadMore}
            onClick={loadMore}
            disabled={loading}
            aria-busy={loading}
            data-testid="load-more-invoices"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
